import { inject, Injectable, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthenticatedResult, OidcSecurityService, PublicEventsService } from 'angular-auth-oidc-client';
import { firstValueFrom, interval, Observable, of, Subscription } from 'rxjs';
import { NetworkStatusService } from './networkStatus.Service';
import { DataService } from './DataService';
import { Router } from '@angular/router';
import { DependencyInjectionProvider } from './DependecyInjection.provider';
import { Account as ManagerAccount } from '../models/matwins-manager/Account.model';
import { Account as TenantAccount } from '../models/matwins-tenant/Account.model';
import { MatwinsVariablesService } from './matwinsVariables.service';
import { PermissionsService } from './Permissions.service';
import { RuntimeProfileService } from './RuntimeProfileService';


export type BootError = { kind: 'unreachable' } | { kind: 'http'; status: number };

@Injectable({
  providedIn: 'root'
})
export class AuthentificationService {

  userVerified = signal(false);
  isUserAuthenticated: boolean = false;

  /** Erreur bloquante au démarrage ; non nul = aucun compte chargé. */
  bootError = signal<BootError | null>(null);
  bootInProgress = signal(false);

  isAuthenticated: boolean = false;
  //constructor(private di: DependencyInjectionProvider) { }
  isAuthenticated$!: Observable<AuthenticatedResult>;


  private tokenCheckSubscription?: Subscription;
  protected di = inject(DependencyInjectionProvider);

  public permissionsService: PermissionsService = inject(PermissionsService);

  dataService: DataService<ManagerAccount>;


  private tokenCheckIntervalMs = 10000; // Vérifie toutes les 10 secondes
  private refreshThresholdSec = 30; // Seuil de refresh avant expiration



  private authAttempted = false;
  userName: string | undefined;
  currentUser!: ManagerAccount | TenantAccount;

  constructor(private oidcSecurityService: OidcSecurityService,
    private networkStatusService: NetworkStatusService,
    private variablesService: MatwinsVariablesService,
    private publicEventsService: PublicEventsService,
    private router: Router,
    private runtimeProfile: RuntimeProfileService
  ) {
    this.dataService = this.di.getOrCreateDataService(ManagerAccount);




  }

  public signin(): void {
    console.log('[AUTH-SERVICE] signin() called. profil local :', this.runtimeProfile.skipsAuthentication);
    if (this.runtimeProfile.skipsAuthentication) {
      console.log('[AUTH-SERVICE] profil local — bypassing OIDC, authenticating as salim@noramsoft.com');
      this.isUserAuthenticated = true;
      this.userName = 'Salim';
      // Injecter un faux access token pour débloquer le chargement des traductions
      this.variablesService.accessTokenChanged$.set('debug-token');
      // Émettre manuellement l'état authentifié
      this.isAuthenticated$ = of({ isAuthenticated: true, userData: null, accessToken: 'debug-token', idToken: '', configId: '', isRenewProcess: false, allConfigsAuthenticated: [] });
      // Charger le compte depuis l'API (le middleware backend injecte salim@noramsoft.com)
      this.verifyAccountDirect();
      return;
    }


    if (!this.runtimeProfile.skipsAuthentication) {
      this.isAuthenticated$ = this.oidcSecurityService.isAuthenticated$;
      this.initAuth();


      this.oidcSecurityService.isAuthenticated$.subscribe(
        ({ isAuthenticated }) => {

          this.isAuthenticated = isAuthenticated;
        }
      );

      this.oidcSecurityService.getAccessToken().subscribe(token => {
        // console.log('Access Token:', token);
      });

      this.oidcSecurityService.userData$.subscribe(userData => {
        // console.log('User Data:', userData);
        this.userName = userData.userData?.name;
      });

      this.networkStatusService.isOnline().subscribe(isOnline => {
        if (isOnline) {
          console.log('Connexion active, tentative d’authentification...');
          this.initAuth();
        }
      });

      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          console.log('Onglet actif à nouveau, vérification du token...');
          this.initAuth();
        }
      });


      this.startTokenMonitor();
    } else {
      this.isAuthenticated = true;
      this.isAuthenticated$ = of({ isAuthenticated: true, allConfigsAuthenticated: [] });
      this.isUserAuthenticated = true;
      this.userName = "'useAuth = false'";
    }
  }


  private startTokenMonitor(): void {
    this.tokenCheckSubscription = interval(this.tokenCheckIntervalMs).subscribe(async () => {
      if (!this.isAuthenticated) {
        return;
      }
      const token = await firstValueFrom(this.oidcSecurityService.getAccessToken());
      const decoded = this.decodeJwt(token);

      if (decoded && decoded.exp) {
        const expiresAt = decoded.exp * 1000; // en ms
        const now = Date.now();
        const timeLeft = (expiresAt - now) / 1000; // en secondes
        const threshold = await firstValueFrom(this.oidcSecurityService.getConfiguration());
        if (timeLeft < threshold?.renewTimeBeforeTokenExpiresInSeconds!) {
          this.initAuth();
        }
      }
    });
  }

  public authorizeWithReturnUrl(returnUrl?: string): void {
    if (this.runtimeProfile.skipsAuthentication) return;

    // Sauvegarder l'URL si fournie
    if (returnUrl) {
      sessionStorage.setItem('returnUrl', returnUrl);
    }
    // Ou utiliser l'URL courante si pas déjà sauvegardée
    else if (!sessionStorage.getItem('returnUrl') && this.router.url !== '/') {
      sessionStorage.setItem('returnUrl', this.router.url);
    }
    this.oidcSecurityService.authorize();
  }

  private initAuth(): void {
    if (this.runtimeProfile.skipsAuthentication) {
      console.log('DEBUG MODE: IdentityServer skipped. Authenticated as dev-admin.');
      return;
    }

    if (!this.authAttempted) {
      this.authAttempted = true;
      console.log('Initialisation de l’authentification...');

      this.oidcSecurityService.checkAuth().subscribe({
        next: async (result) => {
          //console.log('Session vérifiée :', result);
          if (!result.isAuthenticated) {

            this.oidcSecurityService.forceRefreshSession().subscribe({
              next: ({ accessToken }) => {
                if (accessToken) {
                  console.log('Token rafraîchi');
                  this.authAttempted = false;
                  this.variablesService.accessTokenChanged$.set(accessToken);
                  this.verifyAccount();
                } else {
                  console.warn('Pas de nouveau token, relance du flow');
                  this.authorizeWithReturnUrl();
                }
              },
              error: () => {
                console.warn('Refresh token probablement expiré, relance du flow');
                this.authorizeWithReturnUrl();
              }
            });

            // console.warn('Session expirée, redirection...');
            // this.oidcSecurityService.authorize();
          }
          else {
            //verification si account existe
            this.variablesService.accessTokenChanged$.set(result.accessToken);

            await this.verifyAccount();
          }
        },
        error: (err) => {
          console.error('Erreur lors de la vérification de session', err);
          this.authAttempted = false;
        }
      });
    }
  }


  // Debug - Development Code - Cannot be used in production
  private async verifyAccountDirect() {
    this.bootInProgress.set(true);
    try {
      const result = await this.dataService.execute<ManagerAccount | TenantAccount>('GET', 'checkAccountInDatabase');
      if (result) {
        this.bootError.set(null);
        this.currentUser = result;
        this.userVerified.set(true);
        this.isUserAuthenticated = true;
        this.permissionsService.currentUser = this.currentUser;
        this.permissionsService.isUserAuthenticated = true;
        console.log('[AUTH-SERVICE] DEBUG account loaded:', this.currentUser);
      } else {
        this.bootError.set(null);
        console.warn('[AUTH-SERVICE] DEBUG: Aucun compte trouvé pour salim@noramsoft.com');
        this.router.navigate(['/unauthorized']);
      }
    } catch (err) {
      // status 0 = echec reseau (API arretee, DNS, CORS)
      const status = err instanceof HttpErrorResponse ? err.status : -1;
      this.bootError.set(status === 0 ? { kind: 'unreachable' } : { kind: 'http', status });
      this.isUserAuthenticated = false;
      console.error('[AUTH-SERVICE] Echec chargement du compte', err);
    } finally {
      this.bootInProgress.set(false);
    }
  }

  /** Relance le chargement du compte sans recharger la page. */
  public retryBoot(): void {
    if (this.bootInProgress()) return;
    this.verifyAccountDirect();
  }
  // Debug - Development Code - Cannot be used in production

  private async verifyAccount() {
    if (!this.userVerified()) {
      this.bootInProgress.set(true);
      try {
        const user = await firstValueFrom(this.oidcSecurityService.getUserData());

        // const params = { page: 1, size: 10, search: null, sortBy: 'Email', sortOrder: "asc" as "asc", searchColumns: null };
        const result = await this.dataService.execute<ManagerAccount | TenantAccount>('GET', 'checkAccountInDatabase');
        if (result) {
          this.bootError.set(null);
          this.currentUser = result;
          //console.log('Compte trouvé :', result.items[0]);
          this.userVerified.set(true);
          this.authAttempted = false;
          this.isUserAuthenticated = true;
          this.permissionsService.currentUser = this.currentUser;
          this.permissionsService.isUserAuthenticated = true;
        }
        else {
          this.bootError.set(null);
          console.warn('Aucun compte trouvé, redirection vers la page de création de compte...');
          // Rediriger vers la page de création de compte
          this.router.navigate(['/unauthorized']);
          this.authAttempted = false;
          this.isUserAuthenticated = false;

          this.permissionsService.currentUser = null;
          this.permissionsService.isUserAuthenticated = false;
        }
      } catch (err) {
        const status = err instanceof HttpErrorResponse ? err.status : -1;
        this.bootError.set(status === 0 ? { kind: 'unreachable' } : { kind: 'http', status });
        this.authAttempted = false;
        this.isUserAuthenticated = false;
        console.error('[AUTH-SERVICE] Echec chargement du compte', err);
      } finally {
        this.bootInProgress.set(false);
      }
    }
    else {
      this.authAttempted = false;
      this.isUserAuthenticated = true;
    }
  }

  public async getAccessToken(): Promise<string> {
    if (this.runtimeProfile.skipsAuthentication) {
      // Coherent avec signin() : l'API ignore le jeton sous SkipCheck.
      return 'debug-token';
    }
    try {
      return await firstValueFrom(this.oidcSecurityService.getAccessToken());
    } catch {
      return '';
    }
  }



  logout(): void {
    console.log('start logoff');
    this.oidcSecurityService
      .logoff()
      .subscribe((result) => console.log(result));
  }

  logoutLocal(): void {
    this.oidcSecurityService.logoffLocal();
    this.userVerified.set(false);
    this.isUserAuthenticated = false;
    this.isAuthenticated = false;
  }


  private decodeJwt(token: string): any {
    try {
      const payload = token.split('.')[1];
      const decodedPayload = atob(payload);
      return JSON.parse(decodedPayload);
    } catch (e) {
      console.error('Failed to decode token', e);
      return null;
    }
  }

  ngOnDestroy(): void {
    this.tokenCheckSubscription?.unsubscribe();
  }

}
